import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { enqueueSnackbar } from 'notistack';
import { DialogTitle } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import cs from 'classnames';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import useDebounce from '../../../helpers/useDebounce';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getAllChildOrganizations, getAllOrganizations, patchUpdateGroupWithOrganizations } from '../../../api';

import Search from '../../modules/Search';
import Modal from '../../modules/Modal';
import Avatar from '../../modules/Avatar';
import styles from './UpdateGroupOrgsModal.module.scss';
import { GROUP_WITH_SUBGROUP } from '../../../enums';

type TUpdateGroupOrgsModal = {
  open: boolean;
  switchModal: () => void;
  group: GROUP_WITH_SUBGROUP;
  setFetchTrigger: (n: number) => void;
};

const UpdateGroupOrgsModal: React.FC<TUpdateGroupOrgsModal> = ({ open, switchModal, group, setFetchTrigger }) => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [oldSearch, setOldSearch] = useState('');
  const [ignoreIds, setIgnoreIds] = useState<number[]>([]);
  const [availableOrgs, setAvailableOrgs] = useState<typeof group.organizations>([]);
  const [toAdd, setToAdd] = useState<typeof group.organizations>([]);
  const [toRemove, setToRemove] = useState<typeof group.organizations>([]);
  const { myOrganization } = useAppSelector((state) => state.user);

  useEffect(() => {
      if (group.organizations) {
        setIgnoreIds(group.organizations.map((o) => +o.id));
      }
      setToRemove([]);
      setToAdd([]);
  }, [group.organizations]);

  useEffect(() => {
    if (!open || !myOrganization) return;
    (async () => {
      setLoading(true);
      try {
        const res = await getAllChildOrganizations({
          id: +myOrganization.id,
          page: 0,
          limit: 1000,
          search: '',
        });
        setAvailableOrgs(res.data.documents || []);
      } catch (err: any) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [ open, ignoreIds, myOrganization]);

  const onAddToggle = (org: any) => {
    setToAdd((prev) => (prev.some((o) => o.id === org.id) ? prev.filter((o) => o.id !== org.id) : [...prev, org]));
    setToRemove((prev) => prev.filter((o) => o.id !== org.id));
  };

  const onRemoveToggle = (org: any) => {
    setToRemove((prev) => (prev.some((o) => o.id === org.id) ? prev.filter((o) => o.id !== org.id) : [...prev, org]));
    setToAdd((prev) => prev.filter((o) => o.id !== org.id));
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await patchUpdateGroupWithOrganizations(group.id, {
        orgsToAdd: toAdd.map((o) => o.id),
        orgsToRemove: toRemove.map((o) => o.id),
      });
      setFetchTrigger(Date.now());
      switchModal();
      if (toRemove.length === group.organizations.length && toAdd.length === 0) {
        enqueueSnackbar('Success, group is deleted, because there are 0 organisations',
          {
            variant: 'success',
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom'
            }
          }
        )
      }
    } catch (err: any) {
      if (err?.response?.data?.message) {
        enqueueSnackbar(err?.response?.data?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    switchModal();
    setSearch('');
    setAvailableOrgs([]);
    setToAdd([]);
    setToRemove([]);
    setOldSearch('');
  };

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose} loading={loading}>
      <div className={styles.container}>
        <div className={styles.modalTitle}>Update Group Organisations</div>
        <div className={styles.modalTitleOrg}>{group.title}</div>

        {toAdd.length > 0 && (
          <Tooltip
            title={toAdd.map((o) => (
              <div key={o.id}>{o.name}</div>
            ))}
          >
            <div className={styles.tooltipSelected}>To Add: {toAdd.length}</div>
          </Tooltip>
        )}
        {toRemove.length > 0 && (
          <Tooltip
            title={toRemove.map((o) => (
              <div key={o.id}>{o.name}</div>
            ))}
          >
            <div className={cs(styles.tooltipSelected, styles.remove)}>To Remove: {toRemove.length}</div>
          </Tooltip>
        )}

        <div className={styles.innerContent}>
          <div>
            <div className={styles.subtitle}>Available Organisations</div>
            <Search onChange={(e) => setSearch(e.target.value)} placeHolder="Search" />
            <div className={styles.searchedList}>
              {availableOrgs
                .filter((org) => !ignoreIds.includes(+org.id))
                .filter(
                  (org) =>
                    org.location.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
                    org.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
                )
                .map((org) => (
                  <div key={org.id} className={styles.orgObject}>
                    <Avatar avatar={org.image_url} name={org.name} />
                    <div className={styles.orgInfo}>
                      <div className={styles.name}>{org.name}</div>
                      {org.location && <div className={styles.location}>{org.location}</div>}
                    </div>
                    <input
                      type="checkbox"
                      checked={!!toAdd.find((o) => o.id === org.id)}
                      onChange={() => onAddToggle(org)}
                      className={styles.toggle}
                    />
                  </div>
                ))}
            </div>
          </div>

          <div>
            <div className={styles.subtitle}>Current Organisations</div>
            <Search onChange={(e) => setOldSearch(e.target.value)} placeHolder="Search" />
            <div className={styles.oldOrgs}>
              {group.organizations
                .filter((org) => org.name.toLowerCase().includes(oldSearch.toLowerCase()))
                .filter(
                  (org) =>
                    org.location.toLocaleLowerCase().includes(oldSearch.toLocaleLowerCase()) ||
                    org.name.toLocaleLowerCase().includes(oldSearch.toLocaleLowerCase()),
                )
                .map((org) => (
                  <div key={org.id} className={styles.orgObject}>
                    <Avatar avatar={org.image_url} name={org.name} />
                    <div className={styles.orgInfo}>
                      <div className={styles.name}>{org.name}</div>
                      {org.location && <div className={styles.location}>{org.location}</div>}
                    </div>
                    <input
                      type="checkbox"
                      checked={!toRemove.find((o) => o.id === org.id)}
                      onChange={() => onRemoveToggle(org)}
                      className={styles.toggle}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className={styles.actionBtns}>
          <ContainedButton onClick={onSubmit} disabled={loading || (toAdd.length === 0 && toRemove.length === 0)}>
            Save
          </ContainedButton>
          <OutlinedButton kind="default" onClick={onClose}>
            Cancel
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateGroupOrgsModal;
